import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAppOption } from '~/types/option'
import type { IReplyReviewMacro, IReviewReplyMacroPayload } from '~/types/review'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FORM_ERRORS } from '~/const'
import useApps from '~/hooks/useApps'
import {
  keys as replyMacroKeys,
  useCreateReviewReplyMacroMutation,
  useReviewsReplyMacrosNamesQuery,
  useUpdateReviewReplyMacroMutation,
} from '~/queries/reviews-reply-macros'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption, YupAppOption } from '~/utils/yup'

import './../styles.scss'

const MODAL_ID = 'manage-reply-macro-modal'

interface FormValues {
  applications: IAppOption[]
  content: string
  macroName: IOption | null
}

const defaultCreateValues: FormValues = {
  applications: [],
  content: '',
  macroName: null,
}

const validationSchema = Yup.object().shape({
  applications: Yup.array().of(YupAppOption).required().min(1, FORM_ERRORS.AT_LEAST_ONE),
  content: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  macroName: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
})

interface FormProps {
  macro?: IReplyReviewMacro
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const ManageReplyMacroModalForm = ({ macro, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const { appsOptions, isAppsLoading } = useApps()

  const defaultValues: FormValues = macro
    ? {
        applications:
          macro.applications
            ?.map(
              ({ app_key, device_type }) =>
                appsOptions.find((el) => el.appKey === app_key && el.deviceType === device_type) as IAppOption
            )
            .filter((el) => el !== undefined) || [],
        content: macro.content,
        macroName: {
          label: macro.name,
          value: macro.name,
        },
      }
    : defaultCreateValues

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { data: macroNames } = useReviewsReplyMacrosNamesQuery()

  const { mutateAsync: createReviewReplyMacro } = useCreateReviewReplyMacroMutation()

  const { mutateAsync: updateReviewReplyMacro } = useUpdateReviewReplyMacroMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const selectedApps = values.applications?.map(({ value }) => JSON.parse(value)) || []
      const isAllApps = values.applications?.length === appsOptions?.length
      const payload: IReviewReplyMacroPayload = {
        applications: isAllApps ? [] : selectedApps,
        content: values.content,
        is_all_apps: isAllApps,
        name: values.macroName?.value || '',
      }
      if (macro) {
        await updateReviewReplyMacro({ macroId: macro.id, payload })
      } else {
        await createReviewReplyMacro(payload)
      }
      await queryClient.invalidateQueries({ queryKey: replyMacroKeys.all })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="applications"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                components={{ Option: CustomAppOption }}
                error={errors.applications?.message}
                isLoading={isAppsLoading}
                label="Applications"
                options={appsOptions}
                placeholder="Select one or more applications"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="macroName"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.macroName?.message}
                label="Name"
                options={macroNames?.map((name) => ({ label: name, value: name })) || []}
                placeholder="Select or type a name and hit enter"
                required
              />
            )}
          />
          <TextArea
            {...register('content')}
            error={errors.content?.message}
            label="Macro"
            placeholder="Enter content"
            required
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageReplyMacroModal = ({ isOpen, macro, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal className="reviews__manage-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{macro ? 'Edit' : 'Create'} macro</ModalHeader>
      <ManageReplyMacroModalForm macro={macro} onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default ManageReplyMacroModal
